import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import {
  Box,
  Typography,
  Paper,
  CircularProgress,
  Alert,
  useTheme,
  Button,
  Chip,
  Avatar,
  Stack,
  Card,
  CardContent,
  alpha,
  TextField,
  Autocomplete
} from '@mui/material';
import { Wrapper } from '@googlemaps/react-wrapper';
import {
  Refresh,
  Circle,
  CheckCircle,
  Warning,
  Map as MapIcon,
  People,
  FilterList,
  Search,
  PauseCircle,
  Block
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { configurationService } from '../../services/configurationService';
import { monitoramentoService, MapaPontoServico } from '../../services/monitoramentoService';

const validateCoordinates = (coordString: any): { lat: number; lng: number } | null => {
  if (!coordString || typeof coordString !== 'string') return null;
  const parts = coordString.split(',');
  if (parts.length !== 2) return null;

  const lat = parseFloat(parts[0].trim());
  const lng = parseFloat(parts[1].trim());

  if (!isNaN(lat) && !isNaN(lng) && lat >= -90 && lat <= 90 && lng >= -180 && lng <= 180) {
    return { lat, lng };
  }
  return null;
};

const STATUS_COLORS = {
  online: '#4CAF50',
  offline: '#F44336',
  bloqueado: '#FF9800',
  suspenso: '#9E9E9E',
  default: '#FF9800'
} as const;

const FILTER_OPTIONS = [
  { key: 'Todos', label: 'Todos', color: undefined },
  { key: 'Online', label: 'Online', color: STATUS_COLORS.online },
  { key: 'Offline', label: 'Offline', color: STATUS_COLORS.offline },
  { key: 'Bloqueado', label: 'Bloqueado', color: STATUS_COLORS.bloqueado },
  { key: 'Suspenso', label: 'Suspenso', color: STATUS_COLORS.suspenso }
] as const;

const getServiceMarkerColor = (status: string, conexao?: string | null): string => {
  const statusLower = status?.toLowerCase().trim();

  if (statusLower === 'suspenso') return STATUS_COLORS.suspenso;
  if (statusLower === 'bloqueado') return STATUS_COLORS.bloqueado;
  if (statusLower === 'ativo') {
    if (conexao === 'Online') return STATUS_COLORS.online;
    return STATUS_COLORS.offline;
  }

  return STATUS_COLORS.default;
};

const getClienteNome = (servico: MapaPontoServico) =>
  servico.contrato?.cliente?.nome || `Cliente #${servico.contrato?.cliente_id || servico.contrato_id}`;

const matchesFilter = (servico: MapaPontoServico, filter: string) => {
  if (filter === 'Todos') return true;
  const statusLower = servico.status?.toLowerCase().trim();
  if (filter === 'Online') return statusLower === 'ativo' && servico.conexao === 'Online';
  if (filter === 'Offline') return statusLower === 'ativo' && servico.conexao !== 'Online';
  if (filter === 'Bloqueado') return statusLower === 'bloqueado';
  if (filter === 'Suspenso') return statusLower === 'suspenso';
  return true;
};

const getFilterLabel = (servico: MapaPontoServico) => {
  const statusLower = servico.status?.toLowerCase().trim();
  if (statusLower === 'bloqueado') return 'Bloqueado';
  if (statusLower === 'suspenso') return 'Suspenso';
  if (statusLower === 'ativo') return servico.conexao === 'Online' ? 'Online' : 'Offline';
  return servico.status;
};

const darkMapStyles: google.maps.MapTypeStyle[] = [
  { elementType: 'geometry', stylers: [{ color: '#1f2933' }] },
  { elementType: 'labels.text.fill', stylers: [{ color: '#d5dbe3' }] },
  { elementType: 'labels.text.stroke', stylers: [{ color: '#111827' }] },
  { featureType: 'administrative', elementType: 'geometry.stroke', stylers: [{ color: '#4b5563' }] },
  { featureType: 'landscape', elementType: 'geometry', stylers: [{ color: '#18212b' }] },
  { featureType: 'poi', elementType: 'geometry', stylers: [{ color: '#243241' }] },
  { featureType: 'poi.park', elementType: 'geometry', stylers: [{ color: '#173524' }] },
  { featureType: 'road', elementType: 'geometry', stylers: [{ color: '#354252' }] },
  { featureType: 'road', elementType: 'geometry.stroke', stylers: [{ color: '#111827' }] },
  { featureType: 'road.highway', elementType: 'geometry', stylers: [{ color: '#4b5563' }] },
  { featureType: 'road.highway', elementType: 'geometry.stroke', stylers: [{ color: '#1f2937' }] },
  { featureType: 'transit', elementType: 'geometry', stylers: [{ color: '#263445' }] },
  { featureType: 'water', elementType: 'geometry', stylers: [{ color: '#0f2537' }] },
  { featureType: 'water', elementType: 'labels.text.fill', stylers: [{ color: '#9fb3c8' }] }
];

interface StatCardProps {
  label: string;
  value: number;
  color?: string;
  icon: React.ReactNode;
  active: boolean;
  onClick: () => void;
}

const StatCard: React.FC<StatCardProps> = ({ label, value, color, icon, active, onClick }) => (
  <Card
    elevation={active ? 4 : 0}
    onClick={onClick}
    sx={{
      cursor: 'pointer',
      height: '100%',
      border: '2px solid',
      borderColor: active ? (color || 'primary.main') : 'divider',
      bgcolor: active ? alpha(color || '#2196F3', 0.08) : 'background.paper',
      transition: 'all 0.2s ease',
      '&:hover': {
        transform: 'translateY(-2px)',
        boxShadow: 3,
        borderColor: color || 'primary.main'
      }
    }}
  >
    <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between">
        <Box>
          <Typography variant="caption" color="text.secondary" fontWeight={600} textTransform="uppercase" letterSpacing={0.5}>
            {label}
          </Typography>
          <Typography variant="h4" fontWeight={800} sx={{ color: color || 'primary.main', lineHeight: 1.2, mt: 0.5 }}>
            {value}
          </Typography>
        </Box>
        <Avatar
          sx={{
            width: 44,
            height: 44,
            bgcolor: alpha(color || '#2196F3', 0.12),
            color: color || 'primary.main'
          }}
        >
          {icon}
        </Avatar>
      </Stack>
    </CardContent>
  </Card>
);

const createMarkerIcon = (color: string, highlighted = false): google.maps.Symbol => ({
  path: window.google.maps.SymbolPath.CIRCLE,
  fillColor: color,
  fillOpacity: highlighted ? 1 : 0.92,
  strokeColor: '#FFFFFF',
  strokeWeight: highlighted ? 3.5 : 2.5,
  scale: highlighted ? 14 : 9
});

interface MapComponentProps {
  servicos: MapaPontoServico[];
  navigate: ReturnType<typeof useNavigate>;
  isDarkMode: boolean;
  focusTarget?: {
    lat: number;
    lng: number;
    servicoId: number;
    name: string;
    markerColor: string;
    token: number;
  } | null;
}

const MapComponent: React.FC<MapComponentProps> = ({ servicos, navigate, isDarkMode, focusTarget }) => {
  const mapRef = useRef<HTMLDivElement>(null);
  const mapInstanceRef = useRef<google.maps.Map | null>(null);
  const markersDataRef = useRef<Map<number, { marker: google.maps.Marker; servico: MapaPontoServico; color: string }>>(new Map());
  const infoWindowRef = useRef<google.maps.InfoWindow | null>(null);
  const highlightRingRef = useRef<google.maps.Marker | null>(null);
  const highlightPulseRef = useRef<number | null>(null);
  const highlightBounceRef = useRef<number | null>(null);
  const highlightInfoWindowRef = useRef<google.maps.InfoWindow | null>(null);

  const [center] = useState({ lat: -15.7801, lng: -47.9292 });
  const [zoom] = useState(5);

  const clearHighlightEffects = useCallback(() => {
    highlightRingRef.current?.setMap(null);
    highlightRingRef.current = null;

    if (highlightPulseRef.current) {
      window.clearInterval(highlightPulseRef.current);
      highlightPulseRef.current = null;
    }

    if (highlightBounceRef.current) {
      window.clearTimeout(highlightBounceRef.current);
      highlightBounceRef.current = null;
    }

    highlightInfoWindowRef.current?.close();
    highlightInfoWindowRef.current = null;

    if (window.google?.maps) {
      markersDataRef.current.forEach(({ marker, color }) => {
        marker.setAnimation(null);
        marker.setZIndex(undefined);
        marker.setIcon(createMarkerIcon(color));
      });
    }
  }, []);

  const applyHighlight = useCallback((target: NonNullable<MapComponentProps['focusTarget']>) => {
    if (!mapInstanceRef.current || !window.google?.maps) return;

    const map = mapInstanceRef.current;
    const entry = markersDataRef.current.get(target.servicoId);
    if (!entry) return;

    const { marker } = entry;

    marker.setIcon(createMarkerIcon(target.markerColor, true));
    marker.setZIndex(window.google.maps.Marker.MAX_ZINDEX + 1);
    marker.setAnimation(window.google.maps.Animation.BOUNCE);

    highlightBounceRef.current = window.setTimeout(() => {
      marker.setAnimation(null);
    }, 2800);

    let pulseScale = 20;
    let growing = true;
    const ringMarker = new window.google.maps.Marker({
      position: { lat: target.lat, lng: target.lng },
      map,
      clickable: false,
      icon: {
        path: window.google.maps.SymbolPath.CIRCLE,
        fillColor: target.markerColor,
        fillOpacity: 0.18,
        strokeColor: target.markerColor,
        strokeWeight: 2.5,
        strokeOpacity: 0.7,
        scale: pulseScale
      },
      zIndex: window.google.maps.Marker.MAX_ZINDEX
    });
    highlightRingRef.current = ringMarker;

    highlightPulseRef.current = window.setInterval(() => {
      if (growing) {
        pulseScale += 1.2;
        if (pulseScale >= 32) growing = false;
      } else {
        pulseScale -= 1.2;
        if (pulseScale <= 20) growing = true;
      }

      ringMarker.setIcon({
        path: window.google.maps.SymbolPath.CIRCLE,
        fillColor: target.markerColor,
        fillOpacity: 0.1 + (pulseScale - 20) * 0.008,
        strokeColor: target.markerColor,
        strokeWeight: 2.5,
        strokeOpacity: 0.45 + (32 - pulseScale) * 0.015,
        scale: pulseScale
      });
    }, 90);

    const infoWindow = new window.google.maps.InfoWindow({
      content: `
        <div style="padding:2px 4px;text-align:center;">
          <div style="font-weight:800;font-size:13px;color:#111827;">${target.name}</div>
          <div style="font-size:11px;color:#6b7280;margin-top:2px;">Cliente selecionado</div>
        </div>
      `
    });
    infoWindow.open(map, marker);
    highlightInfoWindowRef.current = infoWindow;
  }, []);

  useEffect(() => {
    if (!mapRef.current || !window.google?.maps) return;

    const map = new window.google.maps.Map(mapRef.current, {
      center,
      zoom,
      mapTypeId: 'roadmap',
      mapTypeControl: true,
      streetViewControl: true,
      fullscreenControl: true,
      backgroundColor: isDarkMode ? '#111827' : '#ffffff',
      styles: isDarkMode ? darkMapStyles : []
    });

    mapInstanceRef.current = map;
    infoWindowRef.current = new window.google.maps.InfoWindow();

    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition((position) => {
        map.setCenter({ lat: position.coords.latitude, lng: position.coords.longitude });
        map.setZoom(13);
      });
    }

    return () => {
      clearHighlightEffects();
      markersDataRef.current.forEach(({ marker }) => marker.setMap(null));
      markersDataRef.current.clear();
    };
  }, [center, zoom, clearHighlightEffects]);

  useEffect(() => {
    if (!mapInstanceRef.current) return;

    mapInstanceRef.current.setOptions({
      backgroundColor: isDarkMode ? '#111827' : '#ffffff',
      styles: isDarkMode ? darkMapStyles : []
    });
  }, [isDarkMode]);

  useEffect(() => {
    if (!mapInstanceRef.current || !window.google?.maps) return;

    const map = mapInstanceRef.current;

    clearHighlightEffects();
    markersDataRef.current.forEach(({ marker }) => marker.setMap(null));
    markersDataRef.current.clear();

    const bounds = new window.google.maps.LatLngBounds();
    let hasValidPoints = false;

    servicos.forEach(servico => {
      const coords = validateCoordinates(servico.coordenadas_inst);
      if (coords) {
        const contratoId = servico.contrato?.id || servico.contrato_id;
        const tituloContrato = servico.contrato?.titulo || `Contrato ${contratoId}`;
        const clienteId = servico.contrato?.cliente?.id || servico.contrato?.cliente_id;
        const nomeCliente = getClienteNome(servico);
        const markerColor = getServiceMarkerColor(servico.status, servico.conexao);

        const marker = new window.google.maps.Marker({
          position: coords,
          map,
          title: `${nomeCliente} - ${servico.tipo}`,
          icon: createMarkerIcon(markerColor)
        });

        marker.addListener('click', () => {
          const conexaoLabel = servico.conexao ? servico.conexao : 'N/A';
          const content = `
            <div style="padding: 8px;">
              <strong>Servico:</strong> ${servico.tipo.toUpperCase()}<br/>
              <strong>Plano:</strong> ${servico.plano?.nome || 'N/A'}<br/>
              <strong>Contrato:</strong> <a href="#" style="color: #2196F3; text-decoration: none;" data-contract-id="${contratoId}">${tituloContrato}</a><br/>
              <strong>Cliente:</strong> <a href="#" style="color: #2196F3; text-decoration: none;" data-client-id="${clienteId || 'unknown'}">${nomeCliente}</a><br/>
              <strong>Status:</strong> ${servico.status}<br/>
              <strong>Conexao:</strong> ${conexaoLabel}
            </div>
          `;
          infoWindowRef.current?.setContent(content);
          infoWindowRef.current?.open(map, marker);

          setTimeout(() => {
            const contractLink = document.querySelector(`[data-contract-id="${contratoId}"]`) as HTMLElement;
            if (contractLink) {
              contractLink.addEventListener('click', (e) => {
                e.preventDefault();
                navigate(`/contracts/${contratoId}`);
              });
            }

            if (clienteId) {
              const clientLink = document.querySelector(`[data-client-id="${clienteId}"]`) as HTMLElement;
              if (clientLink) {
                clientLink.addEventListener('click', (e) => {
                  e.preventDefault();
                  navigate(`/clients/${clienteId}`);
                });
              }
            }
          }, 0);
        });

        markersDataRef.current.set(servico.id, { marker, servico, color: markerColor });
        bounds.extend(coords);
        hasValidPoints = true;
      }
    });

    if (hasValidPoints && !focusTarget) {
      map.fitBounds(bounds);
      const listener = window.google.maps.event.addListener(map, 'idle', function() {
        if (map.getZoom()! > 18) map.setZoom(18);
        window.google.maps.event.removeListener(listener);
      });
    }
  }, [servicos, navigate, focusTarget, clearHighlightEffects]);

  useEffect(() => {
    if (!mapInstanceRef.current || !window.google?.maps) return;

    clearHighlightEffects();

    if (!focusTarget) return;

    const map = mapInstanceRef.current;
    map.panTo({ lat: focusTarget.lat, lng: focusTarget.lng });
    map.setZoom(17);
    applyHighlight(focusTarget);

    return () => clearHighlightEffects();
  }, [focusTarget, servicos, applyHighlight, clearHighlightEffects]);

  return <Box ref={mapRef} sx={{ width: '100%', height: '100%' }} />;
};

const MonitoramentoClientesPage: React.FC = () => {
  const theme = useTheme();
  const primario = theme.palette.primary.main;
  const navigate = useNavigate();
  const [googleMapsApiKey, setGoogleMapsApiKey] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeFilter, setActiveFilter] = useState<string>('Todos');
  const [servicos, setServicos] = useState<MapaPontoServico[]>([]);
  const [searchInput, setSearchInput] = useState('');
  const [focusTarget, setFocusTarget] = useState<{
    lat: number;
    lng: number;
    servicoId: number;
    name: string;
    markerColor: string;
    token: number;
  } | null>(null);

  const servicosWithCoords = useMemo(
    () => servicos.filter(s => validateCoordinates(s.coordenadas_inst)),
    [servicos]
  );

  const stats = useMemo(() => ({
    total: servicosWithCoords.length,
    online: servicosWithCoords.filter(s => matchesFilter(s, 'Online')).length,
    offline: servicosWithCoords.filter(s => matchesFilter(s, 'Offline')).length,
    bloqueado: servicosWithCoords.filter(s => matchesFilter(s, 'Bloqueado')).length,
    suspenso: servicosWithCoords.filter(s => matchesFilter(s, 'Suspenso')).length
  }), [servicosWithCoords]);

  const filteredServicos = useMemo(
    () => servicosWithCoords.filter(s => matchesFilter(s, activeFilter)),
    [servicosWithCoords, activeFilter]
  );

  const loadData = async () => {
    try {
      setLoading(true);
      setError('');
      const config = await configurationService.getConfigurationByKey('google_maps_api_key');
      if (config && config.valor) {
        setGoogleMapsApiKey(config.valor);
      } else {
        setError('Chave da API do Google Maps nao configurada no sistema.');
        return;
      }

      const data = await monitoramentoService.getClientes();
      setServicos(data || []);
    } catch (err: any) {
      console.error('Erro ao inicializar mapa:', err);
      setError('Erro ao carregar dados do mapa. ' + (err.message || ''));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const handleFilterChange = useCallback((filter: string) => {
    setActiveFilter(filter);
    setFocusTarget(null);
  }, []);

  const handleSearchSelect = useCallback((servico: MapaPontoServico | null) => {
    if (!servico) return;

    const coords = validateCoordinates(servico.coordenadas_inst);
    if (!coords) return;

    const isVisible = filteredServicos.some(s => s.id === servico.id);
    if (!isVisible) {
      setActiveFilter('Todos');
    }

    setFocusTarget({
      lat: coords.lat,
      lng: coords.lng,
      servicoId: servico.id,
      name: getClienteNome(servico),
      markerColor: getServiceMarkerColor(servico.status, servico.conexao),
      token: Date.now()
    });
    setSearchInput('');
  }, [filteredServicos]);

  if (loading) {
    return (
      <Box sx={{ p: 3, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '60vh', gap: 2 }}>
        <CircularProgress size={48} thickness={4} />
        <Typography color="text.secondary" fontWeight={500}>Carregando mapa de clientes...</Typography>
      </Box>
    );
  }

  if (error || !googleMapsApiKey) {
    return (
      <Box sx={{ p: 3 }}>
        <Paper
          elevation={0}
          sx={{
            p: 4,
            borderRadius: 3,
            textAlign: 'center',
            border: '1px solid',
            borderColor: 'divider'
          }}
        >
          <Avatar sx={{ width: 56, height: 56, mx: 'auto', mb: 2, bgcolor: alpha(theme.palette.error.main, 0.1), color: 'error.main' }}>
            <MapIcon />
          </Avatar>
          <Typography variant="h5" fontWeight={700} sx={{ mb: 1 }}>Monitoramento de Clientes</Typography>
          <Alert severity="error" sx={{ mb: 3, textAlign: 'left' }}>{error || 'Chave do Google Maps invalida.'}</Alert>
          <Button variant="contained" onClick={loadData} startIcon={<Refresh />}>Tentar Novamente</Button>
        </Paper>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 0.5, height: '100%', display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Paper
        elevation={2}
        sx={{
          mb: 0,
          borderRadius: 2,
          overflow: 'hidden',
          background: `linear-gradient(135deg, ${alpha(primario, 0.04)} 0%, ${alpha(theme.palette.secondary.main, 0.04)} 100%)`,
        }}
      >
        <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Avatar sx={{ bgcolor: alpha(primario, 0.12), color: primario, width: 40, height: 40 }}>
              <People sx={{ fontSize: 22 }} />
            </Avatar>
            <Box>
              <Typography variant="h6" fontWeight={800} sx={{ fontSize: '1.05rem', lineHeight: 1.2 }}>
                Monitoramento de Clientes
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.72rem' }}>
                Acompanhe servicos ativos, conexoes e status dos clientes no mapa
              </Typography>
            </Box>
          </Box>
          <Box
            display="flex"
            gap={0.5}
            alignItems="center"
            sx={{ width: { xs: '100%', md: 'auto' }, minWidth: { md: 420 }, flex: { xs: 1, md: 'unset' } }}
          >
            <Autocomplete
              size="small"
              options={servicosWithCoords}
              getOptionLabel={(option) => getClienteNome(option)}
              inputValue={searchInput}
              onInputChange={(_, value) => setSearchInput(value)}
              onChange={(_, value) => handleSearchSelect(value)}
              filterOptions={(options, { inputValue }) => {
                const query = inputValue.trim().toLowerCase();
                return options.filter(option =>
                  getClienteNome(option).toLowerCase().includes(query) ||
                  (option.contrato?.titulo || '').toLowerCase().includes(query) ||
                  option.tipo.toLowerCase().includes(query)
                );
              }}
              noOptionsText="Nenhum cliente encontrado"
              sx={{ flex: 1, minWidth: 0 }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder="Pesquisar cliente pelo nome..."
                  InputProps={{
                    ...params.InputProps,
                    startAdornment: (
                      <>
                        <Search sx={{ ml: 0.5, mr: 0.5, color: 'action.active', fontSize: 18 }} />
                        {params.InputProps.startAdornment}
                      </>
                    ),
                  }}
                  sx={{
                    bgcolor: 'background.paper',
                    '& .MuiOutlinedInput-root': { height: 32 },
                  }}
                />
              )}
              renderOption={(props, option) => (
                <Box component="li" {...props} key={option.id}>
                  <Stack direction="row" spacing={1.5} alignItems="center" sx={{ py: 0.25 }}>
                    <Circle sx={{ fontSize: 10, color: getServiceMarkerColor(option.status, option.conexao) }} />
                    <Box>
                      <Typography variant="body2" fontWeight={600}>{getClienteNome(option)}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {option.tipo.toUpperCase()} · {getFilterLabel(option)}
                      </Typography>
                    </Box>
                  </Stack>
                </Box>
              )}
            />
            <Button
              variant="outlined"
              size="small"
              onClick={loadData}
              startIcon={<Refresh sx={{ fontSize: 18 }} />}
              disabled={loading}
              sx={{ flexShrink: 0, whiteSpace: 'nowrap' }}
            >
              Atualizar Mapa
            </Button>
          </Box>
        </Box>
      </Paper>

      {/* Stats Cards */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: 'repeat(2, 1fr)',
            sm: 'repeat(3, 1fr)',
            lg: 'repeat(5, 1fr)'
          },
          gap: 2
        }}
      >
        <StatCard
          label="Total no mapa"
          value={stats.total}
          icon={<People />}
          active={activeFilter === 'Todos'}
          onClick={() => handleFilterChange('Todos')}
        />
        <StatCard
          label="Online"
          value={stats.online}
          color={STATUS_COLORS.online}
          icon={<CheckCircle />}
          active={activeFilter === 'Online'}
          onClick={() => handleFilterChange('Online')}
        />
        <StatCard
          label="Offline"
          value={stats.offline}
          color={STATUS_COLORS.offline}
          icon={<Warning />}
          active={activeFilter === 'Offline'}
          onClick={() => handleFilterChange('Offline')}
        />
        <StatCard
          label="Bloqueado"
          value={stats.bloqueado}
          color={STATUS_COLORS.bloqueado}
          icon={<Block />}
          active={activeFilter === 'Bloqueado'}
          onClick={() => handleFilterChange('Bloqueado')}
        />
        <StatCard
          label="Suspenso"
          value={stats.suspenso}
          color={STATUS_COLORS.suspenso}
          icon={<PauseCircle />}
          active={activeFilter === 'Suspenso'}
          onClick={() => handleFilterChange('Suspenso')}
        />
      </Box>

      {/* Map */}
      <Paper
        elevation={3}
        sx={{
          flex: 1,
          minHeight: 520,
          overflow: 'hidden',
          borderRadius: 3,
          position: 'relative',
          border: '1px solid',
          borderColor: 'divider'
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            top: 16,
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 1,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 1,
            pointerEvents: 'none'
          }}
        >
          <Paper
            elevation={4}
            sx={{
              px: 1.5,
              py: 1,
              borderRadius: 2,
              pointerEvents: 'auto',
              backdropFilter: 'blur(8px)',
              bgcolor: alpha(theme.palette.background.paper, 0.92)
            }}
          >
            <Stack direction="row" spacing={0.75} alignItems="center" justifyContent="center" sx={{ mb: 0.75 }}>
              <FilterList fontSize="small" color="action" />
              <Typography variant="caption" fontWeight={700} color="text.secondary">
                Filtro: {FILTER_OPTIONS.find(f => f.key === activeFilter)?.label}
              </Typography>
              <Chip label={filteredServicos.length} size="small" color="primary" sx={{ height: 20, fontSize: '0.7rem', fontWeight: 700 }} />
            </Stack>
            <Stack direction="row" spacing={1.5} justifyContent="center" flexWrap="wrap" useFlexGap>
              {FILTER_OPTIONS.filter(f => f.key !== 'Todos').map(f => (
                <Stack key={f.key} direction="row" spacing={0.5} alignItems="center">
                  <Circle sx={{ fontSize: 10, color: f.color }} />
                  <Typography variant="caption" color="text.secondary">{f.label}</Typography>
                </Stack>
              ))}
            </Stack>
          </Paper>
        </Box>
        <Wrapper apiKey={googleMapsApiKey}>
          <MapComponent
            servicos={filteredServicos}
            navigate={navigate}
            isDarkMode={theme.palette.mode === 'dark'}
            focusTarget={focusTarget}
          />
        </Wrapper>
      </Paper>
    </Box>
  );
};

export default MonitoramentoClientesPage;
